// Feature Flags - Easy-to-use global functions
// This provides a simple API for checking feature flags using server-side embedded data

import featureFlagsService from './services/feature_flags_service';

/**
 * Check if a feature flag is enabled
 * @param {string} flagName - Name of the feature flag
 * @param {Object} context - Additional context (ignored - uses server context)
 * @param {boolean} useLiveCheck - If true, makes a live API call instead of using cached data
 * @returns {Promise<boolean>|boolean} Whether the flag is enabled (Promise only if useLiveCheck=true)
 * 
 * @example
 * // Basic usage - uses current user's server-side context from cached data (synchronous)
 * if (isFeatureEnabled('bauer_goal_gear')) {
 *   // Feature is enabled for current user (from page load cache)
 * }
 * 
 * // Live check - makes fresh API call (async)
 * if (await isFeatureEnabled('bauer_goal_gear', {}, true)) {
 *   // Feature is enabled based on current live server state
 * }
 */
function isFeatureEnabled(flagName, context = {}, useLiveCheck = false) {
  return featureFlagsService.isEnabled(flagName, context, useLiveCheck);
}

/**
 * Check if a feature flag is enabled with a live API call
 * @param {string} flagName - Name of the feature flag
 * @returns {Promise<boolean>} Whether the flag is enabled
 * 
 * @example
 * // Always makes a fresh API call
 * if (await isFeatureEnabledLive('bauer_goal_gear')) {
 *   // Feature is enabled based on current live server state
 * }
 */
function isFeatureEnabledLive(flagName) {
  return featureFlagsService.isEnabled(flagName, {}, true);
}

/**
 * Get all feature flags
 * @returns {Array} Array of all feature flags with their status
 * 
 * @example
 * const flags = getAllFeatureFlags();
 * flags.forEach(flag => {
 *   console.log(`${flag.name}: ${flag.enabled}`);
 * });
 */
function getAllFeatureFlags() {
  return featureFlagsService.getAllFlags();
}

/**
 * Get only enabled feature flags
 * @returns {Array} Array of enabled feature flag names
 * 
 * @example
 * const enabledFlags = getEnabledFeatureFlags();
 * console.log('Enabled features:', enabledFlags);
 */
function getEnabledFeatureFlags() {
  const flags = featureFlagsService.getAllFlags();
  return flags.filter(flag => flag.enabled).map(flag => flag.name);
}

/**
 * Get all available feature flag names (enabled and disabled)
 * @returns {Array} Array of all feature flag names
 * 
 * @example
 * const allFlagNames = getAllFeatureFlagNames();
 * console.log('All available flags:', allFlagNames);
 */
function getAllFeatureFlagNames() {
  const flags = featureFlagsService.getAllFlags();
  return flags.map(flag => flag.name);
}

/**
 * Refresh feature flags from server (reloads the page)
 * 
 * @example
 * refreshFeatureFlags(); // This will reload the page
 */
function refreshFeatureFlags() {
  featureFlagsService.refresh();
}

/**
 * Get current user context
 * @returns {Object} User context object
 * 
 * @example
 * const context = getFeatureFlagContext();
 * console.log('Current user:', context.userEmail);
 */
function getFeatureFlagContext() {
  return featureFlagsService.getContext();
}

/**
 * Debug feature flags
 * Shows comprehensive information about feature flags state
 * 
 * @example
 * debugFeatureFlags();
 */
function debugFeatureFlags() {
  return featureFlagsService.debug();
}

/**
 * Test a specific feature flag
 * Shows current status and context information
 * 
 * @param {string} flagName - Name of the feature flag to test
 * @example
 * testFeatureFlag('bauer_goal_gear');
 */
function testFeatureFlag(flagName) {
  console.log(`🧪 Testing feature flag: ${flagName}`);
  
  try {
    // Get current status (uses server-side context)
    const isEnabled = isFeatureEnabled(flagName);
    console.log(`  - Status: ${isEnabled ? '✅ ENABLED' : '❌ DISABLED'}`);
    
    // Show current user context
    const context = getFeatureFlagContext();
    console.log(`  - Context:`, context);
    
    // Show all flags for comparison
    const allFlags = getAllFeatureFlags();
    const flagData = allFlags.find(f => f.name === flagName);
    if (flagData) {
      console.log(`  - Flag data:`, flagData);
    } else {
      console.log(`  - ⚠️ Flag "${flagName}" not found in embedded data`);
    }
    
  } catch (error) {
    console.error(`❌ Error testing feature flag "${flagName}":`, error);
  }
}

/**
 * Load feature flags on page load
 * This ensures flags are cached and ready to use
 */
function initializeFeatureFlags() {
  try {
    console.log('🚩 Initializing feature flags...');
    featureFlagsService.loadFlags();
    console.log('✅ Feature flags initialized');
  } catch (error) {
    console.error('❌ Failed to initialize feature flags:', error);
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', initializeFeatureFlags);

// Also initialize immediately if DOM is already loaded
if (document.readyState !== 'loading') {
  initializeFeatureFlags();
}

// Make functions available globally for console testing
window.isFeatureEnabled = isFeatureEnabled;
window.isFeatureEnabledLive = isFeatureEnabledLive;
window.getAllFeatureFlags = getAllFeatureFlags;
window.getEnabledFeatureFlags = getEnabledFeatureFlags;
window.getAllFeatureFlagNames = getAllFeatureFlagNames;
window.refreshFeatureFlags = refreshFeatureFlags;
window.getFeatureFlagContext = getFeatureFlagContext;
window.debugFeatureFlags = debugFeatureFlags;
window.testFeatureFlag = testFeatureFlag;

// Export for module usage
export {
  isFeatureEnabled,
  isFeatureEnabledLive,
  getAllFeatureFlags,
  getEnabledFeatureFlags,
  getAllFeatureFlagNames,
  refreshFeatureFlags,
  getFeatureFlagContext,
  debugFeatureFlags,
  testFeatureFlag,
  initializeFeatureFlags
};

